import { Directive, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

import { ChartCaptureService } from '../services/chart-capture.service';
import {
  ChartScreenshotService,
  ScreenshotOutcome,
  screenshotFilename,
} from '../services/chart-screenshot.service';
import { TranslationService } from '../i18n/translation.service';
import { ToastService, ToastSeverity } from '../services/toast.service';

/**
 * The camera button, on the anchor that already exists.
 *
 * A DIRECTIVE AND NOT A COMPONENT, for one concrete reason: `a.link7` — the camera
 * icon — is declared in each screen's OWN stylesheet under emulated
 * encapsulation. Moving the anchor into a child component would put it outside
 * that scope and the icon would vanish from all four screens. A directive attaches
 * to the existing element, so every screen keeps its own styling untouched.
 *
 * WHAT THIS REPLACES: `<a [routerLink]="" class="link7"></a>` with NO click
 * handler, on all four chart screens. The capture service and the download helper
 * both already existed and were fully wired; nothing had ever been connected to
 * the icon.
 */
@Directive({ selector: '[appChartScreenshot]' })
export class ChartScreenshotDirective {
  /** The ChartCaptureService name of the surface this button captures. */
  @Input('appChartScreenshot') chart = '';

  /**
   * The human name of that chart, used in the tooltip and the filename.
   *
   * REQUIRED, AND THE TOOLTIP NAMES IT, because on the graph screen two charts are
   * visible at once and one button captures the waterfall only. Desktop avoids the
   * ambiguity by putting each camera physically beside its one chart
   * (`GraphControl` binds a single `Target`); a screen-level control bar cannot, so
   * the affordance has to say which chart it means. Otherwise a user who sees two
   * charts, clicks one camera and gets one image cannot tell that was intended.
   */
  @Input() chartLabel = '';

  /** Project name, for the filename only. */
  @Input() projectName: string | null = null;

  /**
   * Fires after every attempt, carrying the READY-TO-SHOW sentence as well as the
   * outcome.
   *
   * The message is built here rather than by each screen so all four say the same
   * thing, and so a screen does not need TranslationService injected purely to
   * phrase it. The raw outcome rides along for anything that needs to branch.
   *
   * RETAINED THOUGH NOTHING BINDS IT TODAY. This directive now SHOWS the outcome
   * itself (see the toast below), so the four chart screens no longer handle it —
   * but the event is the seam anything else would use to react to a capture, and
   * it is what the directive's own spec asserts against. Emitting it costs
   * nothing; deleting it would remove the only observable record of the outcome.
   */
  @Output() screenshotDone = new EventEmitter<{
    outcome: ScreenshotOutcome;
    message: string;
  }>();

  busy = false;

  constructor(
    private chartCapture: ChartCaptureService,
    private screenshots: ChartScreenshotService,
    private translation: TranslationService,
    private toastService: ToastService
  ) {}

  /** True once the surface actually holds something to draw. */
  get ready(): boolean {
    return this.chartCapture.hasRenderedData(this.chart);
  }

  // Matches the house pattern for a disabled affordance (see .tab-disabled in
  // main-layout): greyed and not-allowed, but pointer-events stay ON so the
  // tooltip explaining WHY is still reachable. The click is refused in code.
  @HostBinding('class.tab-disabled') get disabledClass(): boolean {
    return !this.ready || this.busy;
  }

  @HostBinding('attr.aria-disabled') get ariaDisabled(): string | null {
    return !this.ready || this.busy ? 'true' : null;
  }

  /**
   * The tooltip always NAMES THE TARGET, enabled or not.
   *
   * A disabled control with no stated reason reads as a broken one, and this one
   * is legitimately unavailable whenever the chart has no data yet.
   */
  @HostBinding('attr.title') get tooltip(): string {
    if (this.busy) {
      return this.t('ChartShotWorking', { chart: this.chartLabel });
    }
    if (!this.ready) {
      return this.t('ChartShotNoData', { chart: this.chartLabel });
    }
    return this.t('ChartShotCopy', { chart: this.chartLabel });
  }

  @HostListener('click', ['$event'])
  async onClick(event: Event): Promise<void> {
    // The anchor is href-less but still an <a>; without this the router sees a
    // navigation on some of these screens.
    event.preventDefault();
    event.stopPropagation();

    // REFUSED IN CODE, not by pointer-events. Keeping the element clickable is
    // what keeps its tooltip reachable, so the refusal has to live here.
    if (!this.ready || this.busy) {
      return;
    }

    this.busy = true;
    try {
      const outcome = await this.screenshots.capture(
        this.chart,
        screenshotFilename(this.projectName, this.chartLabel)
      );
      const message = screenshotMessage(outcome, (k, p) => this.t(k, p));

      // SHOWN AS A TOAST, HERE, ONCE.
      //
      // Joel: "rather than 'Chart copied to your clipboard.' appearing in that
      // right side tool bar which makes the tool bar wider and pinches the
      // charts, can you present [it] in toast style notification in the upper
      // right?" Each screen used to render this into a div INSIDE
      // .chart-controls, so the confirmation PARTICIPATED IN LAYOUT: showing it
      // widened the toolbar and the charts reflowed. ToastHostComponent is
      // position:fixed, so nothing behind it moves.
      //
      // SAID HERE RATHER THAN BY THE FOUR SCREENS, for the same reason the
      // message itself is built here: four copies of one notification drift, and
      // three of them already had an identical hand-rolled dismissal timer. This
      // also keeps ToastService out of four chart-component constructors — adding
      // it there broke ten specs that construct those components positionally,
      // which was the compiler pointing out that this did not belong to them.
      this.toastService.show(message, screenshotSeverity(outcome));

      this.screenshotDone.emit({ outcome, message });
    } finally {
      // ALWAYS clears. A capture that throws must not leave the button disabled
      // for the rest of the session — a permanently dead button is worse than a
      // failed capture, because nothing says why.
      this.busy = false;
    }
  }

  private t(key: string, params?: Record<string, string | number>): string {
    return this.translation.t(key, params);
  }
}

/**
 * The user-facing sentence for an outcome. Exported so the screens share one
 * wording and a spec can assert it without a DOM.
 */
/**
 * How loudly to say it.
 *
 * The four outcomes are not equally good news, and the toast colour is the only
 * thing carrying that at a glance:
 *   clipboard -> success, it is where you expect it
 *   download  -> warning, it worked but it went SOMEWHERE ELSE and you must look
 *   empty     -> warning, nothing was captured but nothing is broken
 *   failed    -> error, the chart had data and we could not capture it
 *
 * `download` is deliberately not 'success'. The whole reason that outcome names
 * the file and the reason is that the user is about to look in the wrong place;
 * dressing it in the same green as the clipboard would undo that.
 */
export function screenshotSeverity(outcome: ScreenshotOutcome): ToastSeverity {
  switch (outcome.kind) {
    case 'clipboard':
      return 'success';
    case 'download':
    case 'empty':
      return 'warning';
    case 'failed':
      return 'error';
  }
}

export function screenshotMessage(
  outcome: ScreenshotOutcome,
  t: (key: string, params?: Record<string, string | number>) => string
): string {
  switch (outcome.kind) {
    case 'clipboard':
      return t('ChartShotClipboard');
    case 'download':
      // NAMES THE FILE AND SAYS WHY. "Saved instead" with no reason reads as the
      // clipboard silently not working; the reason is what stops a support call.
      return t('ChartShotDownloaded', { file: outcome.filename, reason: outcome.reason });
    case 'empty':
      return t('ChartShotEmpty');
    case 'failed':
      return t('ChartShotFailed', { reason: outcome.reason });
  }
}
